/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { JobContractStartBasedOnType } from '@gh/builder-shared-data';
import {
  CELL_TEXT_CENTER_CLASS,
  code3Formatter,
  createDefaultGridModel,
  suffixFormatterDecorator,
} from '@gh/common-internal-shared';
import { getRouteInitData, NumberTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableRadioColumn,
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import {
  CpStageCc,
  cpStageCcFieldState,
  CpUnusedCc,
  getCpStageCcId,
  getCpUnusedCcId,
} from '../../../resources/scheduling-cp-details.model';

@Component({
  selector: 'gh-scheduling-cp-details-cc-list',
  template: require('./scheduling-cp-details-cc-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulingCpDetailsCcListComponent implements OnInit {
  @Input() editable = true;
  @Input() contractStartBasedOn: JobContractStartBasedOnType;
  @Input() stageCostCenters: CpStageCc[];
  @Input() unusedCostCenters: CpUnusedCc[];

  @Output() move = new EventEmitter<{ from: CpStageCc; to: CpStageCc }>();
  @Output() fromStage = new EventEmitter<CpStageCc>();
  @Output() contractStartChange = new EventEmitter<CpStageCc>();
  @Output() toStage = new EventEmitter<CpUnusedCc>();

  stageCcGridDefinition: GridDefinition;
  unusedCcGridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.stageCcGridDefinition = this.buildStageCcGridDefinition();
    this.unusedCcGridDefinition = this.buildUnusedCcGridDefinition();
  }

  private buildStageCcGridDefinition(): GridDefinition {
    const { cpStageCcLayout } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getCpStageCcId,
      getCellDef: cpStageCcFieldState,
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      options: <any>{
        defaultColDef: {
          sortable: false,
        },
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory<CpStageCc>((from, to) => this.move.emit({ from, to })),
      },
      layout: cpStageCcLayout,
      columns: {
        costCenterCode: { rowDrag: this.editable, valueFormatter: code3Formatter },
        isContractStart: buildEditableRadioColumn(model.getCellAccessor('isContractStart'), {
          onChange: (params) => this.contractStartChange.emit(params.data),
        }),
        costCenterScheduledDays: {
          valueFormatter: suffixFormatterDecorator(
            typedValueFormatter(NumberTypes.Days),
            this.translations.get('lbl_d')),
          cellClass: CELL_TEXT_CENTER_CLASS,
        },
      },
    });
  }

  private buildUnusedCcGridDefinition(): GridDefinition {
    const { cpUnusedCcLayout } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getCpUnusedCcId,
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: cpUnusedCcLayout,
      columns: {
        code: { valueFormatter: code3Formatter },
      },
    });
  }
}
